<template>
    <v-container>
        <v-card class="mx-6 py-6 my-6">
            <v-row>
                <v-col class="center" cols="5">
                    <h4>Subscription Type:</h4>
                </v-col>
                <v-col class="end pr-12" cols="7">
                    <v-item-group :value="membership" active-class="active" mandatory>
                        <v-item>
                            <v-tooltip top class="secondary-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text :class="`partner-btn ${membership == limitedType.number && 'active'}`"
                                        v-bind="attrs" v-on="on" @click="setPartnerType(limitedType.number)">
                                        <inline-svg :src="limitedTypeImage"></inline-svg>
                                    </v-btn>
                                </template>
                                <span>Free Tier</span>
                            </v-tooltip>
                        </v-item>
                        <v-item>
                            <v-tooltip top class="secondary-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text :class="`partner-btn ${membership == premiumType.number && 'active'}`"
                                        v-bind="attrs" v-on="on" @click="setPartnerType(premiumType.number)">
                                        <inline-svg :src="premiumTypeImage"></inline-svg>
                                    </v-btn>
                                </template>
                                <span>Premium Tier</span>
                            </v-tooltip>
                        </v-item>
                        <v-item>
                            <v-tooltip top class="secondary-tooltip">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text :class="`partner-btn ${membership == preferredType.number && 'active'}`"
                                        v-bind="attrs" v-on="on" @click="setPartnerType(preferredType.number)">
                                        <inline-svg :src="establishedPartnerImage"></inline-svg>
                                    </v-btn>
                                </template>
                                <span>Professional Tier</span>
                            </v-tooltip>
                        </v-item>
                    </v-item-group>
                </v-col>
            </v-row>
            <v-row v-if="isContractor">
                <v-col class="center" cols="5">
                    <h4>Availability:</h4>
                </v-col>
                <v-col cols="5" class="center" style="padding: 0px;">
                    <v-card style="margin-top: 1em;"
                        :class="businessSettings.discoverable ? 'published-card' : 'unpublished-card'">
                        <span>{{ businessSettings.discoverable ? 'Discoverable' : 'Hidden' }}</span>
                    </v-card>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="center" cols="5">
                    <h4>Website:</h4>
                </v-col>
                <v-col v-if="isEstablishmentUser" class="center" cols="7">
                    <v-text-field v-if="!isDateNightSite" @change="changed" class="mt-6" v-model="website"
                        outlined></v-text-field>
                    <site-button v-else btnType="text" :marginNumber="3" @click-event="toggleBuilder(true)"
                        colorClasses="secondary-icon" toolTipGravity="BOTTOM" toolTip="Configure Website" iconType="ICON"
                        iconString="fas fa-screwdriver-wrench"></site-button>
                    <site-button v-if="isDateNightSite" btnType="text" :marginNumber="3"
                        @click-event="setSiteBuilder(false)" colorClasses="secondary-icon" toolTipGravity="BOTTOM"
                        toolTip="Disable Site Builder" iconType="ICON" iconString="far fa-window-close"></site-button>
                    <site-button v-else btnType="text" :marginNumber="3" @click-event="setSiteBuilder(true)"
                        colorClasses="secondary-icon" toolTipGravity="BOTTOM" toolTip="Enable Site Builder" iconType="SVG"
                        :iconString="siteBuilderIcon"></site-button>
                    <v-card v-if="isDateNightSite" :class="homePage?.published ? 'published-card' : 'unpublished-card'">
                        <span>{{ homePage.published ? 'Published' : 'Unpublished' }}</span>
                    </v-card>
                    <site-button v-if="settingsChanged" :colNum="2" :marginNumber="3" btnType="text" toolTip="Save"
                        @click-event="saveChanges" colorClasses="secondary-icon" iconColor="primary-icon"
                        toolTipGravity="BOTTOM" iconString="fa-solid fa-circle-check"></site-button>
                </v-col>
                <v-col v-else-if="isContractor" class="center" cols="5" style="padding: 0px;">
                    <v-text-field @change="changed" class="mx-3 mt-6" v-model="website" outlined></v-text-field>
                </v-col>
            </v-row>
            <v-row v-if="isEstablishmentUser && !!clients?.length">
                <v-data-table
                    :headers="clientHeaders"
                    :items-per-page="20"
                    :items="clients"
                ></v-data-table>
            </v-row>
            <v-row>
                <v-col class="center">
                    <site-button :marginNumber="1" :colNum="10" btnType="text" text="Change Business Info" iconType="SVG"
                        colorClasses="secondary-icon" iconColor="primary-icon" @click-event="addressDialog = true"
                        :iconString="businessAddressImage"></site-button>
                </v-col>
                <v-col class="center">
                    <site-button :marginNumber="1" :colNum="10" btnType="text"
                        :text="isContractor ? 'Change Availability' : 'Change Business Hours'" iconType="SVG"
                        colorClasses="secondary-icon" iconColor="primary-icon" @click-event="setHoursDialogState(true)"
                        :iconString="businessHourImage"></site-button>
                </v-col>
                <v-col class="center" v-if="isEstablishmentUser">
                    <site-button :marginNumber="1" :colNum="10" btnType="text" text="Upload Clients" iconType="SVG"
                        colorClasses="secondary-icon" iconColor="primary-icon" @click-event="uploadClientList"
                        :iconString="clientListUploadImage"></site-button>
                </v-col>
            </v-row>
        </v-card>
        <input style="display: none;" @change="clientListUpload" type="file" accept=".csv" id="clientListUpload"
            ref="clientListUpload" />
        <site-builder-dialog @update-page-data="updatePageData" @delete-nav-item="deleteNavItem"
            @set-dialog-control="toggleBuilder" @add-nav-item="addNavItem" :navItems="businessSettings.navItems"
            :showDialog="builderDialog" :pageData="homePage" />
        <v-dialog style="overflow-y: hidden;" width="70%" v-model="hoursDialog">
            <v-card class="dialog-card">
                <v-flex class="inner-card-flex">
                    <v-row class="end">
                        <v-btn @click="setHoursDialogState(false)" icon>
                            <v-icon>fas fa-times</v-icon>
                        </v-btn>
                    </v-row>
                    <business-hours @update-hours="updateBusinessHours" :businessHours="hours"></business-hours>
                </v-flex>
            </v-card>
        </v-dialog>
        <v-dialog style="overflow-y: hidden;" width="70%" v-model="addressDialog">
            <v-card class="dialog-card">
                <v-flex class="inner-card-flex pa-3">
                    <v-row class="end">
                        <v-btn @click="addressDialog = false" icon>
                            <v-icon>fas fa-times</v-icon>
                        </v-btn>
                    </v-row>
                    <v-row dense class="mt-3" style="justify-content: space-between;">
                        <v-col cols="6">
                            <v-row dense style="justify-content: space-between; align-items: center;">
                                <div class="mt-2" style="display: flex; flex-direction: row;">
                                    <v-icon style="margin: 0.5em 1em;">fas fa-store</v-icon>
                                    <h3 class="info-label">Company Info</h3>
                                </div>
                            </v-row>
                            <v-divider></v-divider>
                        </v-col>
                        <v-col cols="6">
                            <site-button :colNum="8" btnType="text" text="Invite Users" @click-event="saveChanges"
                                colorClasses="secondary-icon mb-2" iconColor="primary-icon" iconType="SVG"
                                :iconString="inviteImage"></site-button>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col>
                            <image-card type="CIRCLE" @remove-business-photo="removeLogo" @add-photo="addLogo"
                                :photo="logo"></image-card>
                        </v-col>
                        <v-col style="display: flex; justify-content: center; align-items: center; flex-direction: column;">
                            <h4>Account Type</h4>
                            <div style="padding-top: 0.5em;" class="center">
                                <span style="margin-left: 3.25em">{{ acctType }}</span>
                                <site-button @change="changed" btnType="text" :marginNumber="6" :colNum="2"
                                    colorClasses="primary-icon" toolTip="Request Account Type Change"
                                    iconString="far fa-edit"></site-button>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row class="end">
                        <v-col class="end" cols="5">
                            <v-text-field color="#00006d" v-model="contactNumber" v-mask="'(###) ###-####'"
                                prepend-inner-icon="fas fa-phone" class="text-field-outline" ref="customer-phone"
                                name="customer-phone" label="Contact Number" outlined></v-text-field>
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col class="end" cols="6">
                            <v-text-field label="Slogan (Optional)" outlined ref="slogan" name="slogan" v-model="slogan"
                                @change="changed"></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row dense>
                        <v-col cols="6">
                            <v-row>
                                <v-icon style="margin: 0.5em 1em;">fas fa-address-book</v-icon>
                                <h3 class="info-label">
                                    {{ isContractor ? 'Billing Info (Used To Pay You)' : 'Location Info' }}
                                </h3>
                            </v-row>
                            <v-divider></v-divider>
                        </v-col>
                    </v-row>
                    <v-row dense style="margin-bottom: .5em;">
                        <address-component v-model="businessAddress"></address-component>
                    </v-row>
                    <v-row v-if="isEstablishmentUser" dense class="center" style="margin-bottom: 2.5%;">
                        <v-col cols="7">
                            <h3 style="padding-bottom: .75em" class="vertical-whitespace">Capacity</h3>
                            <v-range-slider dense show-ticks="always" thumb-label="always" :step="1" :min="0" :max="8"
                                v-model="capacity">
                                <template v-slot:thumb-label="item">
                                    <span>{{ capacityChoices[item.value] }}</span>
                                </template>
                            </v-range-slider>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col></v-col>
                        <v-col cols="6">
                            <site-button :colNum="12" btnType="text" text="Submit" @click-event="updateData"
                                colorClasses="secondary-icon" iconColor="primary-icon"
                                iconString="fa-solid fa-circle-check"></site-button>
                        </v-col>
                        <v-col></v-col>
                    </v-row>
                </v-flex>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<style lang="scss" scoped>
@import '../../styles/styles.scss';

.v-text-field__details {
    display: none;
}

.info-label {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
}

::v-deep .v-dialog {
    overflow-y: hidden !important;
    border-radius: 15px;
    height: 90% !important;
    max-height: 95% !important;
    max-width: 80%;
}

.dialog-card {
    height: 100%;
    overflow-y: hidden;
}

.inner-card-flex {
    height: 100%;
    overflow-y: auto;
    margin-left: 0;
    margin-right: 0;
}
</style>

<script>
import PartnerTypeEnums from '../../assets/Enums/PartnerTypeEnum';
import SiteButton from '../../components/common/SiteButton';
import BusinessHours from '../../components/common/Business/BusinessHours';
import BuilderDialog from '../../components/common/SiteBuilderDialog';
import ChargeType from '../../assets/Enums/ChargeTypeEnums';
import PartnerTypeEnum from '../../assets/Enums/PartnerTypeEnum';
import PaymentType from '../../assets/Enums/PaymentType';
import PaymentMethod from '../../assets/Enums/PaymentMethodEnum';
import BusinessAddress from '../../components/common/Business/BusinessAddress'
import ImageCard from '../../components/common/ImageCard';
import SvgInline from 'vue-inline-svg';
import { mask } from 'vue-the-mask';
import FileType from "../../assets/Enums/FileType";

export default {
    directives: {
        mask,
    },
    components: {
        'inline-svg': SvgInline,
        'site-button': SiteButton,
        'business-hours': BusinessHours,
        'site-builder-dialog': BuilderDialog,
        'address-component': BusinessAddress,
        'image-card': ImageCard
    },
    data: () => ({
        partnerType: 0,
        website: '',
        logoImage: null,
        contactNumber: '',
        slogan: '',
        capacity: [],
        hours: [
            { number: 1, opens: '', closes: '' },
            { number: 2, opens: '', closes: '' },
            { number: 3, opens: '', closes: '' },
            { number: 4, opens: '', closes: '' },
            { number: 5, opens: '', closes: '' },
            { number: 6, opens: '', closes: '' },
            { number: 7, opens: '', closes: '' },
        ],
        businessAddress: {
            line1: null,
            line2: null,
            city: null,
            state: {
                abbreviation: '',
                name: '',
            },
            zipCode: null,
        },
        hoursDialog: false,
        addressDialog: false,
        builderDialog: false,
        settingsChanged: false,
        file: null,
        clientHeaders: [
            {
                title: 'Client',
                align: 'start',
                sortable: true,
                key: 'name',
            },
            {
                title: 'Email',
                align: 'start',
                sortable: true,
                key: 'email',
            },
            {
                title: 'Phone Number',
                align: 'start',
                sortable: false,
                key: 'phoneNumber',
            },
            {
                title: 'Group',
                align: 'start',
                sortable: true,
                key: 'group',
            },
            {
                title: 'Birthday',
                align: 'start',
                sortable: true,
                key: 'birthday',
            }
        ]
    }),
    created() {
        const capacity = this.$store.state.partnerData.capacity;
        this.hours = this.$store.getters.userHours;
        this.partnerType = this.$store.getters.partnerType;
        this.slogan = this.$store.state.partnerData.slogan;
        this.website = this.$store.state.partnerData.website;
        this.contactNumber = this.$store.state.partnerData.contactNumber;
        this.businessAddress = this.$store.state.partnerData.businessAddress;
        this.capacity = [
            this.capacityChoices.findIndex(c => c == capacity[0]),
            this.capacityChoices.findIndex(c => c == capacity[1])
        ];
        console.log(this.$store.state.partnerData.capacity);
    },
    methods: {
        addLogo(files) {
            const item = files[0]
            const reader = new FileReader()

            console.log('logo ' + item.name)

            reader.onload = event => {
                this.logoImage = {
                    photoData: event.target.result,
                    photoFile: item,
                    type: FileType.LOGO,
                }
            }
            reader.readAsDataURL(item)
        },
        removeLogo() {
            this.logoImage = null;
        },
        setHoursDialogState(state) {
            console.log('hours ' + state);
            this.hoursDialog = state;
        },
        updateData() {
            this.$store.dispatch('UPDATE_PARTNER_DATA', {
                address: this.businessAddress,
                slogan: this.slogan,
                contactNumber: this.contactNumber,
                capacity: [
                    this.capacityChoices[this.capacity[0]],
                    this.capacityChoices[this.capacity[1]]
                ]
            });
        },
        inviteNewUser() {

        },
        saveChanges() {
            console.log('SUBMIT')
            this.$store.dispatch('SUBMIT_SETTINGS')
            this.settingsChanged = false
        },
        requestAddressChange() {

        },
        updateBusinessHours(hours) {
            console.log('update bus', hours);
            this.$store.dispatch('UPDATE_BUSINESS_HOURS', hours);
        },
        changed() {
            this.settingsChanged = true
        },
        uploadClientList() {
            this.$refs['clientListUpload'].click()
        },
        toggleBuilder(state) {
            this.builderDialog = state;
        },
        setSiteBuilder(state) {
            this.$store.dispatch('SET_SITE_BUILDER_STATE', state)
            this.settingsChanged = true
            if (!state) this.$store.dispatch('UPDATE_PAGE_DATA', { ...this.homePage, published: false });
            console.log('site builder ' + state)
        },
        setPartnerType(partnerType) {
            console.log(partnerType + ' ' + this.$store.getters.partnerType);

            if (partnerType == PartnerTypeEnum.LIMITED.number) {
                console.log('limited');
                const data = {
                    saveInfo: false,
                    paymentStatus: PaymentType.SUBMITTED,
                    method: PaymentMethod.FREE_MODE,
                    charges: [{
                        chargeName: 'FREE_DOWNGRADE', chargeType: ChargeType.UPGRADE,
                        membership: PartnerTypeEnum.LIMITED.number, chargeQty: 0, chargePrice: 0
                    }]
                };
                this.$store.dispatch('SUBMIT_PAYMENT', { ...data, charges: data.charges });
            } else {
                console.log('quick pay');
                this.partnerType = this.$store.getters.partnerType;
                this.$store.dispatch('QUICK_PAY', {
                    chargeName: 'Membership Upgrade',
                    chargeType: ChargeType.UPGRADE,
                    chargeQty: 1,
                    chargePrice: Object.values(PartnerTypeEnum)[partnerType].price,
                    membership: Object.values(PartnerTypeEnum)[partnerType].number
                });
            }
        },
        clientListUpload(events) {
            const uploadedFile = events.target.files[0]
            if (uploadedFile.size < 5242880) {
                this.file = uploadedFile;

                this.$store.dispatch('SUBMIT_CLIENT_LIST', this.file);
            } else {
                console.log('error file too big')
            }
        },
        deleteNavItem(i) {
            this.$store.dispatch('DELETE_NAV_ITEM', i);
        },
        addNavItem(item) {
            this.$store.dispatch('ADD_NAV_ITEM', item);
        },
        updatePageData(data, newImages, removedImages) {
            const page = { ...data, newImages, removedImages };
            console.log('page-data ', page);
            this.$store.dispatch('UPDATE_PAGE_DATA', page);
        }
    },
    computed: {
        // businessSlug() {

        // },
        clients() {
            return this.$store.state.clients;
        },
        logo() {
            return this.logoImage ? this.logoImage.photoData : require('../../assets/Add-Logo.svg');
        },
        isDateNightSite() {
            return this.$store.state.partnerSettings.siteBuilder
        },
        membership() {
            return this.$store.getters.partnerType;
        },
        limitedType() {
            return PartnerTypeEnums.LIMITED
        },
        premiumType() {
            return PartnerTypeEnums.PREMIUM
        },
        preferredType() {
            return PartnerTypeEnums.PREFERRED
        },
        isEstablishmentUser() {
            return this.$store.getters.isEstablishment;
        },
        isPlannerUser() {
            return this.$store.getters.isPlanner;
        },
        isContractor() {
            return this.$store.getters.isContractor;
        },
        acctType() {
            return this.$store.getters.acctType || ''
        },
        businessSettings() {
            return this.$store.state.partnerSettings
        },
        homePage() {
            return this.$store.state.homePage;
        },
        businessData() {
            return this.$store.state.partnerData
        },
        inviteImage() {
            return require('../../assets/Send-Invite.svg');
        },
        limitedTypeImage() {
            return require('../../assets/Small/New-Partner.svg')
        },
        premiumTypeImage() {
            return require('../../assets/Small/Premium-Partner.svg')
        },
        establishedPartnerImage() {
            return require('../../assets/Small/Preferred-Partner.svg')
        },
        businessAddressImage() {
            return require('../../assets/Business-Address.svg')
        },
        businessHourImage() {
            return require('../../assets/Business-Hours.svg')
        },
        clientListUploadImage() {
            return require('../../assets/Upload-Client-List.svg')
        },
        siteBuilderIcon() {
            return require('../../assets/Site-Builder.svg')
        },
        capacityChoices() {
            return [0, 100, 200, 350, 500, 750, 1000, 1500, 2000, 2500]
        }
    },
}
</script>
